import yaml from "js-yaml";
import JSZip from "jszip";
import mime from "mime/lite";
import { API_BASE_URL } from "../constants";
import { createLocalDraftKey } from "./scenarioDraftIdentity";

const DEFAULT_CONTEXT_FILE = "context.md";

type ArchiveYamlObject = Record<string, unknown>;
type ArchiveZipInput = Blob | ArrayBuffer | Uint8Array | string;

interface ImportedArchiveDocument {
  documentKey?: string;
  name: string;
  description: string;
  instructions?: string;
  objectKey?: string;
  importedObjectKey?: string;
  stagedFilename?: string;
  importedFile?: File;
}

interface ImportedArchivePersona {
  personaKey?: string;
  name: string;
  role: string;
  description: string;
  bio?: string;
  voiceId?: string;
  instructions: string;
  avatarObjectKey?: string;
  avatarFile?: File;
  accessibleDocumentKeys: string[];
  accessibleDocumentIds: string[];
}

export interface ParsedScenarioArchive {
  title: string;
  categoryName: string;
  description: string;
  instructions: string;
  playerRole: string;
  objective: string;
  briefing: string;
  logoObjectKey?: string;
  logoFile?: File;
  heroObjectKey?: string;
  heroFile?: File;
  documents: ImportedArchiveDocument[];
  personas: ImportedArchivePersona[];
}

interface ExportArchiveDocument {
  name: string;
  description: string;
  instructions?: string;
  objectKey?: string;
  sourceUrl?: string;
  stagedFilename?: string;
  importedFile?: File;
  file?: File;
}

interface ExportArchivePersona {
  name: string;
  role: string;
  description?: string;
  bio?: string;
  voiceId?: string | null;
  instructions: string;
  avatarObjectKey?: string;
  avatarSourceUrl?: string;
  avatarFile?: File;
  file?: File;
  accessibleDocumentKeys?: string[];
  accessibleDocuments?: string[];
}

export interface ScenarioArchiveInput {
  slug?: string;
  title: string;
  category: string;
  description: string;
  instructions: string;
  playerRole?: string;
  objective?: string;
  briefing?: string;
  contextPromptFile?: string;
  logoObjectKey?: string;
  logoSourceUrl?: string;
  logoFile?: File | null;
  heroObjectKey?: string;
  heroSourceUrl?: string;
  heroFile?: File | null;
  archiveAssetToken?: string;
  documents: ExportArchiveDocument[];
  personas: ExportArchivePersona[];
}

function assertObject(value: unknown, label: string): ArchiveYamlObject {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${label} must be a YAML object.`);
  }
  return value as ArchiveYamlObject;
}

function asString(value: unknown, fallback: string): string;
function asString(value: unknown, fallback: undefined): string | undefined;
function asString(
  value: unknown,
  fallback: string | undefined,
): string | undefined {
  return typeof value === "string" ? value : fallback;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function filenameFromObjectKey(objectKey?: string): string | undefined {
  if (!objectKey) return undefined;
  return objectKey.split("/").filter(Boolean).at(-1);
}

function filenameFromUrl(url?: string): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    return parsed.pathname.split("/").filter(Boolean).at(-1);
  } catch {
    return undefined;
  }
}

function archiveOwnerPrefix(name: string): string {
  return (name || "Untitled").replace(/[\\/]+/g, "-").trim() || "Untitled";
}

function uniqueArchiveFilenames(
  entries: { filename?: string; ownerName: string }[],
): (string | undefined)[] {
  const filenameCounts = entries.reduce<Map<string, number>>((counts, entry) => {
    if (!entry.filename) return counts;
    counts.set(entry.filename, (counts.get(entry.filename) ?? 0) + 1);
    return counts;
  }, new Map());
  const usedFilenames = new Set<string>();

  return entries.map((entry) => {
    if (!entry.filename) return undefined;
    const isDuplicate = (filenameCounts.get(entry.filename) ?? 0) > 1;
    const baseFilename = isDuplicate
      ? `${archiveOwnerPrefix(entry.ownerName)}: ${entry.filename}`
      : entry.filename;
    let uniqueFilename = baseFilename;
    let counter = 2;
    while (usedFilenames.has(uniqueFilename)) {
      const extensionIndex = baseFilename.lastIndexOf(".");
      if (extensionIndex > 0) {
        uniqueFilename = `${baseFilename.slice(
          0,
          extensionIndex,
        )} (${counter})${baseFilename.slice(extensionIndex)}`;
      } else {
        uniqueFilename = `${baseFilename} (${counter})`;
      }
      counter += 1;
    }
    usedFilenames.add(uniqueFilename);
    return uniqueFilename;
  });
}

function contentTypeFromFilename(filename: string): string {
  return mime.getType(filename) || "application/octet-stream";
}

function pathCandidates(objectKey: string | undefined, preferredDir: string) {
  const filename = filenameFromObjectKey(objectKey);
  if (!filename) return [];
  const candidates = [filename, `${preferredDir}/${filename}`];
  if (objectKey) candidates.push(objectKey);
  return [...new Set(candidates)];
}

async function fileFromZip(
  zip: JSZip,
  candidates: string[],
): Promise<File | undefined> {
  for (const candidate of candidates) {
    const entry = zip.file(candidate);
    if (entry) {
      const blob = await entry.async("blob");
      const name = candidate.split("/").filter(Boolean).at(-1) ?? candidate;
      return new File([blob], name, {
        type: blob.type || contentTypeFromFilename(name),
      });
    }
  }
  return undefined;
}

function slugify(value: string): string {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
  return slug || "scenario";
}

function objectKeyFor(
  kind: "document" | "image",
  slug: string,
  filename?: string,
  fallback?: string,
): string | undefined {
  if (fallback) return fallback;
  if (!filename) return undefined;
  const dir = kind === "document" ? "docs" : "images";
  return `scenarios/${slug}/${dir}/${filename}`;
}

async function fileFromUrl(
  url: string | undefined,
  filename: string | undefined,
  token?: string,
): Promise<File | undefined> {
  if (!url || !filename) return undefined;
  const request = assetFetchRequest(url);
  const response = await fetch(request.url, {
    ...(token && request.proxied
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {}),
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch archive asset: ${response.status}`);
  }
  const blob = await response.blob();
  return new File([blob], filename, {
    type: blob.type || "application/octet-stream",
  });
}

function assetFetchRequest(url: string): { url: string; proxied: boolean } {
  try {
    const parsed = new URL(url);
    if (
      parsed.protocol === "https:" &&
      (parsed.hostname === "storage.googleapis.com" ||
        parsed.hostname.endsWith(".storage.googleapis.com"))
    ) {
      return {
        url: `${API_BASE_URL}/scenarios/assets/fetch?url=${encodeURIComponent(url)}`,
        proxied: true,
      };
    }
  } catch {
    // Fall through to the original URL so fetch surfaces the real failure.
  }
  return { url, proxied: false };
}

async function addFileToZip(
  zip: JSZip,
  path: string,
  file: File,
): Promise<void> {
  zip.file(path, await file.arrayBuffer());
}

function normalizeArchivePath(path: string): string {
  return path.replace(/^\.\/+/, "").replace(/^\/+/, "");
}

async function loadRequiredText(
  zip: JSZip,
  path: string,
  label: string,
): Promise<string> {
  const normalized = normalizeArchivePath(path);
  const entry = zip.file(normalized);
  if (!entry) {
    throw new Error(`${label} not found in archive or malformed: ${normalized}`);
  }
  return entry.async("string");
}

function parseYaml(yamlText: string, label: string): unknown {
  try {
    return yaml.load(yamlText);
  } catch (error) {
    if (error instanceof yaml.YAMLException) {
      const location = error.mark
        ? ` at line ${error.mark.line + 1}, column ${error.mark.column + 1}`
        : "";
      throw new Error(`Failed to parse ${label}${location}: ${error.reason}`);
    }
    throw new Error(`Failed to parse ${label}.`);
  }
}

async function readableZipInput(
  fileOrBlob: ArchiveZipInput,
): Promise<ArrayBuffer | Uint8Array | string> {
  if (fileOrBlob instanceof Blob) {
    return fileOrBlob.arrayBuffer();
  }
  return fileOrBlob;
}

export async function parseScenarioArchive(
  fileOrBlob: ArchiveZipInput,
): Promise<ParsedScenarioArchive> {
  const zip = await JSZip.loadAsync(await readableZipInput(fileOrBlob));
  const scenarioYaml = await loadRequiredText(zip, "scenario.yaml", "scenario.yaml");
  const personasYaml = await loadRequiredText(zip, "personas.yaml", "personas.yaml");
  const scenario = assertObject(parseYaml(scenarioYaml, "scenario.yaml"), "scenario.yaml");
  const personas = asArray(parseYaml(personasYaml, "personas.yaml"));
  const contextPromptFile = asString(
    scenario.contextPromptFile,
    DEFAULT_CONTEXT_FILE,
  );
  const instructions = await loadRequiredText(
    zip,
    contextPromptFile,
    "Context prompt file",
  );
  const logoObjectKey = asString(scenario.logoObjectKey, undefined);
  const logoFile = await fileFromZip(
    zip,
    pathCandidates(logoObjectKey, "images"),
  );
  const heroObjectKey = asString(scenario.heroObjectKey, undefined);
  const heroFile = await fileFromZip(
    zip,
    pathCandidates(heroObjectKey, "images"),
  );
  const documents = await Promise.all(
    asArray(scenario.documents).map(async (doc) => {
      const documentObject = assertObject(doc, "Document entry");
      const objectKey = asString(documentObject.objectKey, undefined);
      const importedFile = await fileFromZip(
        zip,
        pathCandidates(objectKey, "docs"),
      );
      const instructions = asString(documentObject.instructions, undefined);
      return {
        documentKey: createLocalDraftKey("document"),
        name: asString(documentObject.name, ""),
        description: asString(documentObject.description, ""),
        ...(instructions ? { instructions } : {}),
        objectKey,
        importedObjectKey: objectKey,
        stagedFilename:
          importedFile?.name ?? filenameFromObjectKey(objectKey) ?? undefined,
        importedFile,
      };
    }),
  );
  const parsedPersonas = await Promise.all(
    personas.map(async (persona) => {
      const personaObject = assertObject(persona, "Persona entry");
      const avatarObjectKey = asString(personaObject.avatarObjectKey, undefined);
      const avatarFile = await fileFromZip(
        zip,
        pathCandidates(avatarObjectKey, "images"),
      );
      return {
        personaKey: createLocalDraftKey("persona"),
        name: asString(personaObject.name, ""),
        role: asString(personaObject.role, ""),
        description: asString(personaObject.description, ""),
        bio: asString(personaObject.bio, undefined),
        voiceId: asString(personaObject.voiceId, undefined),
        instructions: asString(personaObject.instructions, ""),
        avatarObjectKey,
        avatarFile,
        accessibleDocumentKeys: asArray(personaObject.accessibleDocuments).map(
          String,
        ),
        accessibleDocumentIds: [],
      };
    }),
  );

  return {
    title: asString(scenario.title, ""),
    categoryName: asString(scenario.category, ""),
    description: asString(scenario.description, ""),
    instructions,
    playerRole: asString(scenario.playerRole, ""),
    objective: asString(scenario.objective, ""),
    briefing: asString(scenario.briefing, ""),
    logoObjectKey,
    logoFile,
    heroObjectKey,
    heroFile,
    documents,
    personas: parsedPersonas,
  };
}

export async function buildScenarioArchive(
  input: ScenarioArchiveInput,
): Promise<Blob> {
  const zip = new JSZip();
  const slug = slugify(input.slug || input.title || "scenario");
  const contextPromptFile = input.contextPromptFile || DEFAULT_CONTEXT_FILE;
  const originalLogoFilename =
    input.logoFile?.name ??
    filenameFromObjectKey(input.logoObjectKey) ??
    filenameFromUrl(input.logoSourceUrl);
  const originalHeroFilename =
    input.heroFile?.name ??
    filenameFromObjectKey(input.heroObjectKey) ??
    filenameFromUrl(input.heroSourceUrl);
  const documentAssets = (input.documents || []).map((doc) => ({
    doc,
    originalFilename:
      doc.importedFile?.name ||
      doc.file?.name ||
      doc.stagedFilename ||
      filenameFromObjectKey(doc.objectKey) ||
      filenameFromUrl(doc.sourceUrl),
  }));
  const imageAssets = [
    {
      ownerName: "Logo",
      originalFilename: originalLogoFilename,
    },
    {
      ownerName: "Hero",
      originalFilename: originalHeroFilename,
    },
    ...(input.personas || []).map((persona) => ({
      ownerName: persona.name,
      originalFilename:
        persona.avatarFile?.name ||
        persona.file?.name ||
        filenameFromObjectKey(persona.avatarObjectKey) ||
        filenameFromUrl(persona.avatarSourceUrl),
    })),
  ];
  const documentArchiveFilenames = uniqueArchiveFilenames(
    documentAssets.map(({ doc, originalFilename }) => ({
      filename: originalFilename,
      ownerName: doc.name,
    })),
  );
  const imageArchiveFilenames = uniqueArchiveFilenames(
    imageAssets.map(({ ownerName, originalFilename }) => ({
      filename: originalFilename,
      ownerName,
    })),
  );
  const logoFilename = imageArchiveFilenames[0] ?? originalLogoFilename;
  const logoFile =
    input.logoFile ??
    (await fileFromUrl(
      input.logoSourceUrl,
      originalLogoFilename,
      input.archiveAssetToken,
    ));
  const logoFilenameChanged = Boolean(
    originalLogoFilename && logoFilename !== originalLogoFilename,
  );
  const logoObjectKey = objectKeyFor(
    "image",
    slug,
    logoFilename,
    logoFilenameChanged ? undefined : input.logoObjectKey,
  );
  const heroFilename = imageArchiveFilenames[1] ?? originalHeroFilename;
  const heroFile =
    input.heroFile ??
    (await fileFromUrl(
      input.heroSourceUrl,
      originalHeroFilename,
      input.archiveAssetToken,
    ));
  const heroFilenameChanged = Boolean(
    originalHeroFilename && heroFilename !== originalHeroFilename,
  );
  const heroObjectKey = objectKeyFor(
    "image",
    slug,
    heroFilename,
    heroFilenameChanged ? undefined : input.heroObjectKey,
  );
  const documentObjectKeyByOriginal = new Map<string, string>();
  const documents = documentAssets.map(({ doc, originalFilename }, index) => {
    const filename = documentArchiveFilenames[index] ?? originalFilename;
    const filenameChanged = Boolean(
      originalFilename && filename !== originalFilename,
    );
    const objectKey = objectKeyFor(
      "document",
      slug,
      filename,
      filenameChanged ? undefined : doc.objectKey,
    );
    if (doc.objectKey && objectKey) {
      documentObjectKeyByOriginal.set(doc.objectKey, objectKey);
    }
    return {
      name: doc.name,
      description: doc.description,
      ...(doc.instructions ? { instructions: doc.instructions } : {}),
      ...(objectKey ? { objectKey } : {}),
    };
  });
  const personas = (input.personas || []).map((persona, index) => {
    const originalFilename = imageAssets[index + 2]?.originalFilename;
    const filename = imageArchiveFilenames[index + 2] ?? originalFilename;
    const filenameChanged = Boolean(
      originalFilename && filename !== originalFilename,
    );
    const avatarObjectKey = objectKeyFor(
      "image",
      slug,
      filename,
      filenameChanged ? undefined : persona.avatarObjectKey,
    );
    return {
      name: persona.name,
      role: persona.role,
      ...(avatarObjectKey ? { avatarObjectKey } : {}),
      ...(persona.description ? { description: persona.description } : {}),
      ...(persona.bio ? { bio: persona.bio } : {}),
      ...(persona.voiceId ? { voiceId: persona.voiceId } : {}),
      instructions: persona.instructions,
      accessibleDocuments: (
        persona.accessibleDocumentKeys ||
        persona.accessibleDocuments ||
        []
      ).map(
        (objectKey) => documentObjectKeyByOriginal.get(objectKey) ?? objectKey,
      ),
    };
  });
  const scenario = {
    title: input.title,
    category: input.category,
    description: input.description,
    contextPromptFile,
    ...(input.playerRole ? { playerRole: input.playerRole } : {}),
    ...(input.objective ? { objective: input.objective } : {}),
    ...(input.briefing ? { briefing: input.briefing } : {}),
    ...(logoObjectKey ? { logoObjectKey } : {}),
    ...(heroObjectKey ? { heroObjectKey } : {}),
    documents,
  };

  zip.file("scenario.yaml", yaml.dump(scenario, { lineWidth: 100 }));
  zip.file("personas.yaml", yaml.dump(personas, { lineWidth: 100 }));
  zip.file(contextPromptFile, input.instructions || "");

  if (logoFile) {
    await addFileToZip(zip, `images/${logoFilename || logoFile.name}`, logoFile);
  }
  if (heroFile) {
    await addFileToZip(zip, `images/${heroFilename || heroFile.name}`, heroFile);
  }
  for (const [index, { doc, originalFilename }] of documentAssets.entries()) {
    const filename = documentArchiveFilenames[index] ?? originalFilename;
    const file =
      doc.importedFile ||
      doc.file ||
      (await fileFromUrl(
        doc.sourceUrl,
        originalFilename,
        input.archiveAssetToken,
      ));
    if (file) {
      await addFileToZip(zip, `docs/${filename || file.name}`, file);
    }
  }
  for (const [index, persona] of (input.personas || []).entries()) {
    const originalFilename = imageAssets[index + 2]?.originalFilename;
    const filename = imageArchiveFilenames[index + 2] ?? originalFilename;
    const file =
      persona.avatarFile ||
      persona.file ||
      (await fileFromUrl(
        persona.avatarSourceUrl,
        originalFilename,
        input.archiveAssetToken,
      ));
    if (file) {
      await addFileToZip(zip, `images/${filename || file.name}`, file);
    }
  }

  return zip.generateAsync({ type: "blob" }) as Promise<Blob>;
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function scenarioArchiveFilename(title: string): string {
  return `${slugify(title || "scenario")}.zip`;
}
